import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackAnalytics } from "@/lib/analytics";
import { supplementalItems } from "@/lib/pricing";
import { formatCurrency } from "@/lib/utils";
import type { SupplementalItemId } from "@/types/booking";
import type { StepProps } from "@/components/booking/steps/types";
import { StepFrame } from "@/components/booking/steps/StepFrame";

export function SupplementalItemsStep({ state, isLocked, updateState, goNext, goBack }: StepProps) {
  function setQuantity(id: SupplementalItemId, quantity: number) {
    const nextQuantity = Math.min(Math.max(quantity, 0), 5);
    updateState({ supplementalItems: { ...state.supplementalItems, [id]: nextQuantity } });
    trackAnalytics("supplemental item changed", { id, quantity: nextQuantity });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        goNext();
      }}
    >
      <StepFrame
        title="Supplemental items"
        description="Add chargeable items that need separate handling. Quantities are capped at five per item."
        canBack
        onBack={goBack}
        isReadOnly={isLocked}
        mobileSummary={state.pricingResult ? `Current total ${formatCurrency(state.pricingResult.totalPayable)}` : undefined}
      >
        <div className="grid gap-3">
          {supplementalItems.map((item) => {
            const quantity = state.supplementalItems[item.id] ?? 0;
            return (
              <div key={item.id} className="flex flex-col gap-3 rounded-lg border-2 border-accent/10 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-black">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{formatCurrency(item.price)} each</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="icon" onClick={() => setQuantity(item.id, quantity - 1)} aria-label={`Remove ${item.title}`} disabled={isLocked}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="flex h-10 w-12 items-center justify-center rounded-md bg-secondary font-black">{quantity}</span>
                  <Button type="button" variant="outline" size="icon" onClick={() => setQuantity(item.id, quantity + 1)} aria-label={`Add ${item.title}`} disabled={isLocked}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </StepFrame>
    </form>
  );
}
